import React, { useEffect, useState } from 'react';
import { appUrl } from '../../config/appBase';
import './CaseStudyModal.css';

const CaseStudyModal = ({ caseStudy, isOpen, onClose, onOpenFullStudy }) => {
  const [isLiked, setIsLiked] = useState(false);
  const [isAdded, setIsAdded] = useState(false);

  // Helper function to strip HTML tags and decode entities
  const stripHtmlAndDecode = (htmlString) => {
    if (!htmlString) return '';
    // DOMParser, not a detached div: <img onerror> fires even in a detached div.
    const doc = new DOMParser().parseFromString(String(htmlString), 'text/html');
    return (doc.body.textContent || '').trim();
  };

  const getCleanDescription = () => {
    if (!caseStudy?.description) {
      return 'Step into this real-world decision sandbox. Analyze key financial, legal, and operational drivers, test your hypotheses, and defend your recommendations under pressure.';
    }
    const hasHtmlTags = /<[^>]*>/g.test(caseStudy.description);
    if (hasHtmlTags) {
      return stripHtmlAndDecode(caseStudy.description);
    }
    return caseStudy.description;
  };

  const getResolvedCreator = () => {
    if (caseStudy?.creator) {
      if (typeof caseStudy.creator === 'string') return caseStudy.creator;
      if (caseStudy.creator.name) return caseStudy.creator.name;
      if (caseStudy.creator.full_name) return caseStudy.creator.full_name;
    }
    if (caseStudy?.author) return caseStudy.author;
    if (caseStudy?.creator_name) return caseStudy.creator_name;
    if (caseStudy?.created_by) return caseStudy.created_by;

    const sub = (caseStudy?.subject || '').toLowerCase();
    if (sub.includes('law') || sub.includes('legal')) return 'Faculty of Law';
    if (sub.includes('bank') || sub.includes('finance') || sub.includes('invest') || sub.includes('m&a')) return 'M&A Strategy Group';
    if (sub.includes('med') || sub.includes('health')) return 'Clinical Medicine Panel';
    if (sub.includes('comp') || sub.includes('cs') || sub.includes('tech')) return 'Advanced Systems Lab';
    if (sub.includes('eng')) return 'Applied Engineering Panel';
    return 'Ivori Intelligence';
  };

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleWheel = (e) => {
      const modal = document.querySelector('.netflix-modal-container');
      if (modal) {
        if (!modal.contains(e.target)) {
          e.preventDefault();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      window.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('wheel', handleWheel);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setIsLiked(false);
      setIsAdded(false);
    }
  }, [isOpen]);

  if (!isOpen || !caseStudy) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleOpenFullStudy = () => {
    if (caseStudy.is_locked) return;
    onClose();
    onOpenFullStudy(caseStudy.id);
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
  };

  const handleAdd = () => {
    setIsAdded(!isAdded);
  };

  const handleShare = async () => {
    const shareUrl = appUrl(`/case-study/${caseStudy.id}`);
    const shareData = {
      title: caseStudy.title || 'Ivori Case Study',
      text: getCleanDescription(),
      url: shareUrl
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
      } catch (clipboardError) {
        console.error('Clipboard error:', clipboardError);
      }
    }
  };

  const isLocked = caseStudy.is_locked;
  const heroImage =
    caseStudy.images?.large ||
    caseStudy.images?.medium ||
    caseStudy.images?.small ||
    caseStudy.image1920x1080 ||
    caseStudy.image835x420 ||
    caseStudy.image270x380 ||
    'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=600&fit=crop&auto=format&q=80';

  return (
    <div className="netflix-modal-backdrop" onClick={handleBackdropClick}>
      <div className="netflix-modal-container">
        <div className="netflix-modal-content">
          {/* Close button top right */}
          <button
            id="modal-close-btn"
            type="button"
            className="netflix-modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          {/* Hero Banner Section */}
          <div className="netflix-modal-hero">
            <img
              src={heroImage}
              alt={caseStudy.title}
              className="netflix-modal-hero-img"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=600&fit=crop&auto=format&q=80';
              }}
            />
            <div className="netflix-modal-hero-gradient" />

            {/* Content overlayed on bottom of Hero Image */}
            <div className="netflix-modal-hero-bottom">
              <h1 className="netflix-modal-title">{caseStudy.title}</h1>

              <div className="netflix-modal-hero-actions">
                <button
                  id="modal-start-case-btn"
                  type="button"
                  className={`netflix-btn-play ${isLocked ? 'locked' : ''}`}
                  onClick={handleOpenFullStudy}
                  disabled={isLocked}
                  aria-label={isLocked ? "Case study is locked" : "Start case"}
                >
                  {isLocked ? (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      <span>Unlock {caseStudy.required_tier || 'Foundations'}</span>
                    </>
                  ) : (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M5.5 4.86c0-1.12 1.22-1.81 2.18-1.24l11.5 6.78c.95.56.95 1.94 0 2.5l-11.5 6.78c-.96.57-2.18-.12-2.18-1.24V4.86Z" />
                      </svg>
                      <span>Start Case</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Modal Body: 2-Column Layout */}
          <div className="netflix-modal-body">
            <div className="netflix-body-left">
              {(caseStudy.subject || isLocked) && (
                <div className="netflix-meta-pills">
                  {caseStudy.subject && (
                    <span className="netflix-pill-subject">{caseStudy.subject}</span>
                  )}
                  {isLocked && (
                    <span className="netflix-pill-locked">Locked</span>
                  )}
                </div>
              )}

              <div className="netflix-modal-description">
                <p>{getCleanDescription()}</p>
              </div>
            </div>

            <div className="netflix-body-right">
              <div className="netflix-details-list">
                <div className="netflix-details-row">
                  <span className="netflix-detail-label">Subject:</span>
                  <span className="netflix-detail-value">{caseStudy.subject || 'Investment Banking'}</span>
                </div>
                <div className="netflix-details-row">
                  <span className="netflix-detail-label">Industry:</span>
                  <span className="netflix-detail-value">{caseStudy.industry || 'Mergers & Acquisitions'}</span>
                </div>
                <div className="netflix-details-row">
                  <span className="netflix-detail-label">Difficulty:</span>
                  <span className="netflix-detail-value">Intermediate / Advanced</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseStudyModal;